import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Item as ItemModel } from '../../../Models/Item'; // Adjust path as needed
import AuthContext from '../../store/authContext';
import ApiClient, { getImageUrl } from '../../Services/ApiClient';

import LeadForm from '../leadForm';
import '../../style/item.css';
import '../../style/modal.css';
import '../../style/utils.css';

const apiBase = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_API_URL)
  ? (import.meta as any).env.VITE_API_URL.replace(/\/+$/, '')
  : (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

const Item: React.FC = () => {
    const { isLoggedIn, isTokenExpired } = React.useContext(AuthContext); // Get isLoggedIn from AuthContext]
    const { id } = useParams<{ id: string }>();
    const [item, setItem] = useState<ItemModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const token = localStorage.getItem('token');
    const [newItem, setNewItem] = useState<ItemModel | null>(null);

    // helper: setNewItem is used to populate the modal form when editing

    // Function to render text with paragraph breaks
    const renderTextWithParagraphs = (text: string) => {
        return text.split('\n').map((paragraph, index) => (
            <p key={index} className="" style={{ marginBottom: '1em' }}>
                {paragraph}
            </p>
        ));
    };

    useEffect(() => {
        if (!id) return;
        const fetchItem = async () => {
            setLoading(true);
            try {
                const data: any = await ApiClient.get(`/item/${id}`);
                setItem(data);
            } catch (e) {
                setError('ארעה תקלה');
                console.error('ארעה תקלה בעת טעינת הפריט', e);
            } finally {
                setLoading(false);
            }
        };
        fetchItem();
    }, [id]);

    const UpdateItem = async () => {
        if (!item || !newItem) return;
        try {
            const updated: any = await ApiClient.put('/item', newItem);
            setItem(updated);
            setNewItem(null);
            setShowModal(false);
        } catch (e) {
            setError('עדכון הפריט נכשל');
            console.error('עדכון הפריט נכשל', e);
        }
    };
    if (loading) return <div>טוען...</div>;
    if (!item) return <div>לא נמצא פריט</div>;

    // Prefer resolved image filename (may be from Item.ImageFileName or Item.imageName)
    const resolvedFile = item.imageFileName || item.imageName || undefined;
    const imgUrl = resolvedFile ? getImageUrl(resolvedFile) : null;

    return (
        <div className="item-container rtl">
            <div className="item-details">
                <div className="item-header-flex">
                    <h2 className="item-title">{item.title}</h2>
                    {/* Desktop: LeadForm on top right */}
                    <div className="item-leadform-desktop">
                        <LeadForm showDiscountImage={true} />
                    </div>
                </div>
                <div className="item-main-flex">
                    {imgUrl ? (
                        <div style={{flex:'0 0 320px'}}>
                            <img src={imgUrl} alt={item.title} className="item-image" style={{width:'100%', borderRadius:8, objectFit:'cover'}} onError={(e)=>{ const t=e.currentTarget as HTMLImageElement; t.onerror=null; t.classList.add('img-missing'); }} />
                        </div>
                ) : (
                    <div style={{flex:'0 0 320px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f2f2f2', borderRadius:8, padding:20}}>
                        <div style={{color:'#666'}}>תמונה אינה זמינה</div>
                    </div>
                )}

                <div style={{flex:1}}>
                    <div className="item-text">{renderTextWithParagraphs(item.text || '')}</div>

                    {item.researchSummary && (
                        <div className="meta-section" style={{marginTop:12}}>
                            <h4 className="meta-heading">ממצאי מחקר</h4>
                            <p className="research-summary">{item.researchSummary}</p>
                        </div>
                    )}

                    {item.symptoms && item.symptoms.length > 0 && (
                        <div className="meta-section" style={{marginTop:12}}>
                            <h4 className="meta-heading">תסמינים</h4>
                            <ul className="meta-list">
                                {item.symptoms.map((s: string, i: number) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                    )}

                    {item.treatmentMethods && item.treatmentMethods.length > 0 && (
                        <div className="meta-section" style={{marginTop:12}}>
                            <h4 className="meta-heading">שיטות טיפול</h4>
                            <ul className="meta-list">
                                {item.treatmentMethods.map((t: string, i: number) => <li key={i}>{t}</li>)}
                            </ul>
                        </div>
                    )}

                    {isLoggedIn && !isTokenExpired(token) && (
                        <div style={{marginTop:12}}>
                            <button className="btn-edit" onClick={() => { setNewItem(item); setShowModal(true); }}>עדכן</button>
                        </div>
                    )}
                </div>
            </div>
            </div>

            {showModal && newItem && (
                <div className="modal">
                    <h3 className="modal-title">עדכון פריט</h3>
                    <form className="modal-form" onSubmit={(e) => { e.preventDefault(); UpdateItem(); }}>
                        <div className="form-group">
                            <label htmlFor="inp_title">כותרת:</label>
                            <input
                                id="inp_title"
                                type="text"
                                value={newItem.title}
                                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="inp_rank">דירוג:</label>
                            <input
                                id="inp_rank"
                                type="number"
                                value={newItem.ranking}
                                onChange={(e) => setNewItem({ ...newItem, ranking: parseInt(e.target.value) })}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="inp_desc">תיאור:</label>
                            <textarea
                                id="inp_desc"
                                value={newItem.text}
                                onChange={(e) => setNewItem({ ...newItem, text: e.target.value })}
                                className="form-textarea"
                            />
                        </div>

                        <button type="submit" className="btn-submit">שמור</button>
                    </form>
                </div>
            )}
        </div>

    )
};

export default Item;